import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from './Toast';
import { ApiService } from '../utils/api';
import { formatDateTime, istInputToBackend, toIstInputValue } from '../utils/datetime';
import { ChevronDown, ChevronUp, Copy, Download, FileDown, KeyRound, Link2, Pencil, Plus, Power, Trash2, Wand2 } from 'lucide-react';
import { eventRef } from '../utils/eventRef';

const emptyForm = {
  code: '', name: '', maxUses: '', validUntil: '', allowedEmails: '', waivePayment: true, note: '',
  requireAccessCode: false, accessCodes: [], passIds: [],
};

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

export const inviteUrl = (eventId, code, accessCode) => `${window.location.origin}/register/${eventRef(eventId)}?invite=${code}`
  + (accessCode ? `&access=${encodeURIComponent(accessCode)}` : '');

const randomCode = (prefix = '', length = 6) => {
  const bytes = new Uint32Array(length);
  window.crypto.getRandomValues(bytes);
  const body = Array.from(bytes, (n) => CODE_ALPHABET[n % CODE_ALPHABET.length]).join('');
  const clean = prefix.toUpperCase().replace(/[^A-Z0-9_-]/g, '');
  return clean ? `${clean}-${body}` : body;
};

const BENEFITS = [
  { value: 'NONE', label: 'Normal price' },
  { value: 'FREE', label: 'Free registration' },
  { value: 'COUPON', label: 'Discount (coupon)' },
];

export const describeCoupon = (coupon) => {
  if (!coupon) return '';
  const value = coupon.discountValue;
  if (coupon.discountType === 'PERCENTAGE') return `${value}% off`;
  if (coupon.discountType === 'FLAT') return `₹${value} off`;
  if (coupon.discountType === 'FIXED_PAYABLE') return `pay ₹${value}`;
  return '';
};

const AccessCodesEditor = ({ codes, onChange, coupons, waivePayment }) => {
  const [bulk, setBulk] = useState({ count: '10', prefix: '', benefit: 'NONE', couponId: '' });
  const update = (index, patch) => onChange(codes.map((code, i) => (i === index ? { ...code, ...patch } : code)));
  const existing = new Set(codes.map((code) => code.code));

  const addBulk = () => {
    const count = Math.min(Math.max(Number(bulk.count) || 0, 1), 200);
    const next = [];
    while (next.length < count) {
      const code = randomCode(bulk.prefix);
      if (!existing.has(code) && !next.some((c) => c.code === code)) {
        next.push({ code, label: '', benefit: bulk.benefit, couponId: bulk.couponId, maxUses: '1', active: true, usedCount: 0 });
      }
    }
    onChange([...codes, ...next]);
  };

  return (
    <div className="space-y-3">
      {waivePayment && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          The link itself is free, so code benefits don't change the price. Turn off "Free registration" above to give different benefits per code.
        </p>
      )}
      {codes.length > 0 && (
        <div className="space-y-2">
          {codes.map((code, index) => (
            <div key={index} className="grid grid-cols-2 gap-2 rounded-md border bg-white p-2 sm:grid-cols-12 sm:items-center">
              <Input
                aria-label="Access code"
                className="col-span-2 font-mono uppercase sm:col-span-3"
                value={code.code}
                disabled={code.usedCount > 0}
                onChange={(e) => update(index, { code: e.target.value.toUpperCase().replace(/\s/g, '') })}
                placeholder="CODE"
              />
              <Input
                aria-label="Label"
                className="col-span-2 sm:col-span-3"
                value={code.label || ''}
                onChange={(e) => update(index, { label: e.target.value })}
                placeholder="Label (e.g. Faculty)"
              />
              <select
                aria-label="Benefit"
                className="col-span-1 h-10 rounded-md border border-gray-300 bg-white px-2 text-sm sm:col-span-2"
                value={code.benefit || 'NONE'}
                onChange={(e) => update(index, { benefit: e.target.value })}
              >
                {BENEFITS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              {code.benefit === 'COUPON' ? (
                <select
                  aria-label="Coupon"
                  className="col-span-1 h-10 rounded-md border border-gray-300 bg-white px-2 text-sm sm:col-span-2"
                  value={code.couponId || ''}
                  onChange={(e) => update(index, { couponId: e.target.value })}
                >
                  <option value="">Pick coupon…</option>
                  {coupons.map((c) => <option key={c.id} value={c.id}>{c.code} · {describeCoupon(c)}</option>)}
                </select>
              ) : (
                <Input
                  aria-label="Max uses"
                  type="number"
                  min="1"
                  className="col-span-1 sm:col-span-2"
                  value={code.maxUses ?? ''}
                  onChange={(e) => update(index, { maxUses: e.target.value })}
                  placeholder="Max uses"
                />
              )}
              <div className="col-span-2 flex items-center justify-between gap-2 sm:col-span-2 sm:justify-end">
                <span className="text-xs text-gray-500">{code.usedCount || 0} used</span>
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={code.active !== false} onChange={(e) => update(index, { active: e.target.checked })} />
                  On
                </label>
                {!(code.usedCount > 0) && (
                  <button type="button" className="rounded p-1 text-red-600 hover:bg-red-50" aria-label="Remove code"
                    onClick={() => onChange(codes.filter((_, i) => i !== index))}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              {code.benefit === 'COUPON' && (
                <Input
                  aria-label="Max uses"
                  type="number"
                  min="1"
                  className="col-span-2 sm:col-span-3"
                  value={code.maxUses ?? ''}
                  onChange={(e) => update(index, { maxUses: e.target.value })}
                  placeholder="Max uses (blank = unlimited)"
                />
              )}
              <label className="col-span-1 flex items-center gap-1 text-xs text-gray-600 sm:col-span-6">
                <span className="w-12 shrink-0">From</span>
                <Input type="datetime-local" aria-label="Usable from (IST)" value={code.startsAt || ''} onChange={(e) => update(index, { startsAt: e.target.value })} />
              </label>
              <label className="col-span-1 flex items-center gap-1 text-xs text-gray-600 sm:col-span-6">
                <span className="w-12 shrink-0">Until</span>
                <Input type="datetime-local" aria-label="Usable until (IST)" value={code.endsAt || ''} onChange={(e) => update(index, { endsAt: e.target.value })} />
              </label>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline"
          onClick={() => onChange([...codes, { code: randomCode(), label: '', benefit: 'NONE', couponId: '', maxUses: '', active: true, usedCount: 0 }])}>
          <Plus className="mr-1 h-4 w-4" /> Add code
        </Button>
      </div>
      <div className="rounded-md border border-dashed p-3">
        <p className="mb-2 text-xs font-medium text-gray-700">Generate several single-use codes</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <Input aria-label="How many" type="number" min="1" max="200" value={bulk.count}
            onChange={(e) => setBulk((b) => ({ ...b, count: e.target.value }))} />
          <Input aria-label="Prefix" value={bulk.prefix} placeholder="Prefix (optional)"
            onChange={(e) => setBulk((b) => ({ ...b, prefix: e.target.value }))} />
          <select aria-label="Benefit for generated codes" className="h-10 rounded-md border border-gray-300 bg-white px-2 text-sm"
            value={bulk.benefit} onChange={(e) => setBulk((b) => ({ ...b, benefit: e.target.value }))}>
            {BENEFITS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
          {bulk.benefit === 'COUPON' ? (
            <select aria-label="Coupon for generated codes" className="h-10 rounded-md border border-gray-300 bg-white px-2 text-sm"
              value={bulk.couponId} onChange={(e) => setBulk((b) => ({ ...b, couponId: e.target.value }))}>
              <option value="">Pick coupon…</option>
              {coupons.map((c) => <option key={c.id} value={c.id}>{c.code} · {describeCoupon(c)}</option>)}
            </select>
          ) : <span className="hidden sm:block" />}
          <Button type="button" size="sm" variant="outline" className="col-span-2 sm:col-span-1" onClick={addBulk}
            disabled={bulk.benefit === 'COUPON' && !bulk.couponId}>
            <Wand2 className="mr-1 h-4 w-4" /> Generate
          </Button>
        </div>
      </div>
      {coupons.length === 0 && (
        <p className="text-xs text-gray-500">
          To give a discount, first create a coupon for this event under Settings → Coupons, then pick it here.
        </p>
      )}
    </div>
  );
};

const InviteForm = ({ eventId, editing, onDone, coupons }) => {
  const { data: ticketing } = useQuery({
    queryKey: ['ticketingConfig', eventId],
    queryFn: () => ApiService.getTicketingConfig(eventId).catch(() => null),
    staleTime: 60 * 1000,
  });
  const passes = ticketing?.enabled ? ticketing.passes || [] : [];
  const [form, setForm] = useState(editing ? {
    code: editing.code || '',
    name: editing.name || '',
    maxUses: editing.maxUses ?? '',
    validUntil: toIstInputValue(editing.validUntil),
    allowedEmails: (editing.allowedEmails || []).join('\n'),
    waivePayment: editing.waivePayment !== false,
    note: editing.note || '',
    requireAccessCode: !!editing.requireAccessCode,
    passIds: editing.passIds || [],
    accessCodes: (editing.accessCodes || []).map((code) => ({
      ...code, maxUses: code.maxUses ?? '', couponId: code.couponId || '', startsAt: toIstInputValue(code.startsAt), endsAt: toIstInputValue(code.endsAt),
    })),
  } : emptyForm);
  const [busy, setBusy] = useState(false);
  const { showSuccess, showError } = useToast();
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      code: form.code.trim() || null,
      name: form.name,
      note: form.note || null,
      maxUses: form.maxUses === '' ? null : Number(form.maxUses),
      validUntil: istInputToBackend(form.validUntil),
      allowedEmails: form.allowedEmails ? [form.allowedEmails] : [],
      waivePayment: form.waivePayment,
      active: editing ? editing.active : true,
      requireAccessCode: form.requireAccessCode && form.accessCodes.length > 0,
      passIds: form.passIds,
      accessCodes: form.accessCodes.map((code) => ({
        code: code.code,
        label: code.label || null,
        benefit: code.benefit || 'NONE',
        couponId: code.benefit === 'COUPON' ? code.couponId || null : null,
        maxUses: code.maxUses === '' || code.maxUses == null ? null : Number(code.maxUses),
        active: code.active !== false,
        startsAt: istInputToBackend(code.startsAt),
        endsAt: istInputToBackend(code.endsAt),
      })),
    };
    try {
      const saved = editing
        ? await ApiService.updateInviteLink(eventId, editing.id, payload)
        : await ApiService.createInviteLink(eventId, payload);
      if (!editing) {
        try {
          await navigator.clipboard.writeText(inviteUrl(eventId, saved.code));
          showSuccess('Link created and copied');
        } catch (_) {
          showSuccess('Link created');
        }
      } else {
        showSuccess('Link updated');
      }
      onDone();
    } catch (err) {
      showError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-lg border border-gray-200 p-3 sm:p-4 bg-gray-50">
      <div className="grid gap-3 md:grid-cols-2">
        <div className="md:col-span-2">
          <Label htmlFor="inv-name">Invited institute / group *</Label>
          <Input id="inv-name" required maxLength={120} value={form.name} onChange={set('name')} placeholder="e.g. AIIMS Delhi" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="inv-code">Link code</Label>
          <div className="flex min-w-0 items-center rounded-md border border-input bg-white focus-within:ring-2 focus-within:ring-blue-500">
            <span className="hidden shrink-0 pl-3 text-sm text-gray-400 sm:inline">…?invite=</span>
            <input
              id="inv-code"
              value={form.code}
              onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
              placeholder={editing ? '' : 'Leave empty to create one from the name, e.g. aiims-delhi-7k3p'}
              className="h-10 min-w-0 flex-1 rounded-md bg-transparent px-2 text-sm outline-none sm:pl-1"
              maxLength={50}
            />
          </div>
          {editing && editing.usedCount > 0 && form.code !== editing.code && (
            <p className="mt-1 text-xs text-amber-700">Links already shared with the old code will stop working.</p>
          )}
        </div>
        <div>
          <Label htmlFor="inv-max">Max registrations</Label>
          <Input id="inv-max" type="number" min="1" value={form.maxUses} onChange={set('maxUses')} placeholder="Unlimited" />
        </div>
        <div>
          <Label htmlFor="inv-until">Valid until (IST)</Label>
          <Input id="inv-until" type="datetime-local" value={form.validUntil} onChange={set('validUntil')} />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="inv-emails">Only these emails or domains (optional)</Label>
          <Textarea id="inv-emails" rows={3} value={form.allowedEmails} onChange={set('allowedEmails')}
            placeholder={'dean@college.edu\ncollege.edu  (anyone with an @college.edu address)'} />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="inv-note">Internal note</Label>
          <Input id="inv-note" maxLength={500} value={form.note} onChange={set('note')} />
        </div>
      </div>
      {passes.length > 0 && (
        <div className="text-sm">
          <p className="mb-1 font-medium text-gray-900">Premium registration passes these codes unlock</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {passes.map((pass) => (
              <label key={pass.id} className="flex items-center gap-1.5">
                <input type="checkbox" checked={form.passIds.includes(pass.id)}
                  onChange={(e) => setForm((p) => ({ ...p, passIds: e.target.checked ? [...p.passIds, pass.id] : p.passIds.filter((id) => id !== pass.id) }))} />
                {pass.name}
              </label>
            ))}
            <span className="text-xs text-gray-500">(none ticked = any pass)</span>
          </div>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.waivePayment} onChange={(e) => setForm((p) => ({ ...p, waivePayment: e.target.checked }))} />
        Free registration (no payment) for everyone using this link
      </label>

      <div className="space-y-2 border-t pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900"><KeyRound className="h-4 w-4" /> Access codes</h4>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.requireAccessCode} disabled={form.accessCodes.length === 0}
              onChange={(e) => setForm((p) => ({ ...p, requireAccessCode: e.target.checked }))} />
            Require a code to register
          </label>
        </div>
        <p className="text-xs text-gray-500">
          Give each person or group in this institute a code. A code can make registration free or apply one of the event's coupons, and can be limited to a number of uses.
        </p>
        <AccessCodesEditor
          codes={form.accessCodes}
          onChange={(accessCodes) => setForm((p) => ({ ...p, accessCodes }))}
          coupons={coupons}
          waivePayment={form.waivePayment}
        />
      </div>

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={busy}>{editing ? 'Save' : 'Create link'}</Button>
        <Button type="button" size="sm" variant="outline" onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
};

const csvCell = (value) => {
  const text = String(value ?? '');
  return `"${(/^[=+\-@]/.test(text) ? `'${text}` : text).replace(/"/g, '""')}"`;
};

/** One row per access code with its ready-to-share link. */
const downloadCodeLinks = (link, coupons, eventId) => {
  const rows = [['Code', 'Label', 'Benefit', 'Max uses', 'Used', 'Active', 'Link']];
  (link.accessCodes || []).forEach((code) => {
    const coupon = coupons.find((c) => c.id === code.couponId);
    const benefit = code.benefit === 'FREE' ? 'Free' : code.benefit === 'COUPON' ? `${coupon?.code || 'Coupon'} ${describeCoupon(coupon)}`.trim() : 'Normal price';
    rows.push([code.code, code.label || '', benefit, code.maxUses ?? '', code.usedCount || 0, code.active ? 'Yes' : 'No',
      inviteUrl(link.eventId || eventId, link.code, code.code)]);
  });
  const blob = new Blob([`\uFEFF${rows.map((r) => r.map(csvCell).join(',')).join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(link.name || 'invite').replace(/[^A-Za-z0-9]+/g, '_')}_access_code_links.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const InviteLinksDialog = ({ open, onClose, eventId }) => {
  const [mode, setMode] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { data: links = [] } = useQuery({
    queryKey: ['inviteLinks', eventId],
    queryFn: () => ApiService.getInviteLinks(eventId),
    enabled: open && !!eventId,
  });
  const { data: couponPage } = useQuery({
    queryKey: ['eventCoupons', eventId],
    queryFn: () => ApiService.listCoupons(0, 100, eventId),
    enabled: open && !!eventId,
  });
  const coupons = couponPage?.content || [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['inviteLinks', eventId] });

  const copy = async (code, accessCode) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(eventId, code, accessCode));
      showSuccess('Link copied');
    } catch (_) {
      showError('Copy failed; select the link and copy it manually');
    }
  };

  const download = async (link) => {
    setDownloading(link.id);
    try {
      const blob = await ApiService.downloadInviteRegistrations(eventId, link.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(link.name || 'invite').replace(/[^A-Za-z0-9]+/g, '_')}_registrations.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      showError(e.message);
    } finally {
      setDownloading(null);
    }
  };

  const toggle = (link) => ApiService.updateInviteLink(eventId, link.id, { ...link, active: !link.active })
    .then(refresh).catch((e) => showError(e.message));
  const remove = (link) => window.confirm(`Delete the link for ${link.name}?`)
    && ApiService.deleteInviteLink(eventId, link.id).then(refresh).catch((e) => showError(e.message));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invite links</DialogTitle>
          <DialogDescription>
            One link per invited institute, with optional access codes. Invite details are saved on each registration (Invited By, Invite Code, Access Code), so you can search by them.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {mode ? (
            <InviteForm eventId={eventId} editing={mode.link} coupons={coupons} onDone={() => { setMode(null); refresh(); }} />
          ) : (
            <Button size="sm" onClick={() => setMode({})}><Plus className="w-4 h-4 mr-1" /> New invite link</Button>
          )}
          {links.length === 0 && !mode && <p className="text-sm text-gray-500">No invite links yet.</p>}

          {links.length > 0 && (
            <div className="overflow-hidden rounded-lg border">
              <div className="hidden bg-gray-50 px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-500 md:grid md:grid-cols-12 md:gap-2">
                <span className="col-span-5">Institute</span>
                <span className="col-span-2 text-center">Used quantity</span>
                <span className="col-span-2">Limit</span>
                <span className="col-span-3 text-right">Actions</span>
              </div>
              <ul className="divide-y divide-gray-100">
                {links.map(({ link, registrations }) => {
                  const codes = link.accessCodes || [];
                  const isOpen = expanded === link.id;
                  return (
                    <li key={link.id} className={`px-3 py-3 ${link.active ? '' : 'opacity-60'}`}>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-12 md:items-center">
                        <div className="min-w-0 md:col-span-5">
                          <div className="font-medium flex items-center gap-2"><Link2 className="w-4 h-4 shrink-0 text-blue-600" /><span className="truncate">{link.name}</span></div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {link.waivePayment ? 'Free' : 'Paid'}
                            {codes.length ? ` · ${codes.length} access code${codes.length > 1 ? 's' : ''}${link.requireAccessCode ? ' (required)' : ''}` : ''}
                            {link.validUntil ? ` · until ${formatDateTime(link.validUntil)} IST` : ''}
                            {link.allowedEmails?.length ? ` · ${link.allowedEmails.length} allowed email/domain${link.allowedEmails.length > 1 ? 's' : ''}` : ''}
                            {!link.active ? ' · off' : ''}
                          </div>
                          <code className="block text-xs text-gray-600 mt-1 break-all">{inviteUrl(eventId, link.code)}</code>
                        </div>
                        <div className="flex items-center gap-2 md:col-span-2 md:justify-center">
                          <span className="text-xs text-gray-500 md:hidden">Used:</span>
                          <button
                            type="button"
                            onClick={() => download(link)}
                            disabled={downloading === link.id || link.usedCount === 0}
                            className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-default disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500"
                            title={link.usedCount ? 'Download the attendees who registered with this link' : 'No registrations yet'}
                          >
                            {link.usedCount}
                            {link.usedCount > 0 && <Download className="h-3.5 w-3.5" />}
                          </button>
                          {registrations !== link.usedCount && (
                            <span className="text-xs text-gray-500">{registrations} active</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-700 md:col-span-2">
                          <span className="text-xs text-gray-500 md:hidden">Limit: </span>
                          {link.maxUses ? link.maxUses : 'Unlimited'}
                        </div>
                        <div className="flex flex-wrap gap-1 md:col-span-3 md:justify-end">
                          <Button size="sm" variant="outline" onClick={() => copy(link.code)} aria-label="Copy link" title={link.requireAccessCode ? 'Copy link (registrants will be asked for a code)' : 'Copy link'}><Copy className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => setMode({ link })} aria-label="Edit" title="Edit"><Pencil className="w-4 h-4" /></Button>
                          {codes.length > 0 && (
                            <Button size="sm" variant="outline" onClick={() => setExpanded(isOpen ? null : link.id)} aria-label="Access codes">
                              <KeyRound className="w-4 h-4" />{isOpen ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => toggle(link)} aria-label={link.active ? 'Turn off' : 'Turn on'}
                            title={link.active ? 'Turn off: the link stops accepting registrations' : 'Turn on'}
                            className={link.active ? 'text-green-700' : 'text-gray-400'}>
                            <Power className="w-4 h-4" />
                          </Button>
                          {link.usedCount === 0 && (
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(link)} aria-label="Delete"><Trash2 className="w-4 h-4" /></Button>
                          )}
                        </div>
                      </div>
                      {isOpen && (
                        <div className="mt-3 space-y-2 rounded-md bg-gray-50 p-2">
                        <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                          <p className="text-xs text-gray-600">
                            Each code has its own link with the code built in, so registrants never type or see it.
                          </p>
                          <Button size="sm" variant="outline" onClick={() => downloadCodeLinks(link, coupons)}>
                            <FileDown className="mr-1 h-3.5 w-3.5" /> All code links (CSV)
                          </Button>
                        </div>
                        <ul className="space-y-1">
                          {codes.map((code) => {
                            const coupon = coupons.find((c) => c.id === code.couponId);
                            const benefit = code.benefit === 'FREE' ? 'Free'
                              : code.benefit === 'COUPON' ? (coupon ? `${coupon.code} · ${describeCoupon(coupon)}` : 'Coupon') : 'Normal price';
                            return (
                              <li key={code.code} className={`flex flex-wrap items-center justify-between gap-2 text-sm ${code.active ? '' : 'opacity-60'}`}>
                                <span className="min-w-0">
                                  <code className="font-mono font-semibold">{code.code}</code>
                                  {code.label ? <span className="text-gray-600"> · {code.label}</span> : null}
                                  <span className="text-gray-500"> · {benefit} · {code.usedCount}{code.maxUses ? `/${code.maxUses}` : ''} used</span>
                                </span>
                                <Button size="sm" variant="ghost" onClick={() => copy(link.code, code.code)}>
                                  <Copy className="mr-1 h-3.5 w-3.5" /> Link with code
                                </Button>
                              </li>
                            );
                          })}
                        </ul>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default InviteLinksDialog;
